import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Plus, Minus, Coins, Info, Search, Lock } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminInput, AdminTextarea, AdminSelect, EmptyState } from "@/admin/components/ui";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

const initial = { customer_id: "", amount: 1, customer_note: "", admin_note: "", reference: "" };

export default function AdminCredits() {
  const [customers, setCustomers] = useState([]);
  const [tx, setTx] = useState([]);
  const [loading, setLoading] = useState(true);
  const [add, setAdd] = useState(initial);
  const [remove, setRemove] = useState(initial);
  const [filterCust, setFilterCust] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([adminApi.get("/admin/customers"), adminApi.get("/admin/credits")]);
      setCustomers(c.data);
      setTx(t.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (type, form, setForm) => {
    if (!form.customer_id) { toast.error("Select a customer."); return; }
    if (!form.amount || form.amount < 1) { toast.error("Enter a positive amount."); return; }
    try {
      await adminApi.post("/admin/credits", { ...form, type, amount: Number(form.amount) });
      toast.success(type === "add" ? "Credits added." : "Credits removed.");
      setForm(initial);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const filtered = useMemo(() => {
    return tx.filter((t) => {
      if (filterCust !== "all" && t.customer_id !== filterCust) return false;
      if (q && !`${t.customer_email} ${t.note || ""} ${t.reference || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [tx, filterCust, q]);

  const totals = useMemo(() => {
    const added = filtered.filter((t) => t.type === "add").reduce((s, t) => s + (t.amount || 0), 0);
    const removed = filtered.filter((t) => t.type === "remove").reduce((s, t) => s + (t.amount || 0), 0);
    return { added, removed, net: added - removed, count: filtered.length };
  }, [filtered]);

  return (
    <div data-testid="page-admin-credits" className="space-y-6">
      <SEO title="AdminCredits · WelkomBiz Photography" noindex />
      <Panel testId="credits-explainer">
        <div className="flex items-start gap-3">
          <Info className="w-4 h-4 text-[#18d3ff] shrink-0 mt-0.5" />
          <p className="text-sm text-white/65 font-body">
            Credits are <strong>manually assigned</strong> by the admin after customers pay outside the website. Balance updates are
            atomic and transaction-logged — every change appears in the history below with balance before / after.
            Customer-visible notes appear in their dashboard. Admin notes stay internal.
          </p>
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Add */}
        <Panel title="Add Credits" testId="credits-add-panel"
          action={<Coins className="w-4 h-4 text-[#ff2e93]" />}>
          <div className="space-y-4">
            <AdminSelect data-testid="add-credits-customer" label="Customer" value={add.customer_id} onChange={(e) => setAdd({ ...add, customer_id: e.target.value })}>
              <option value="" className="bg-black">— select customer —</option>
              {customers.map((c) => <option key={c.id} value={c.id} className="bg-black">{c.name} {c.surname} · {c.email} · {c.credit_balance ?? 0} cr</option>)}
            </AdminSelect>
            <AdminInput data-testid="add-credits-amount" label="Credit Amount" type="number" min={1} value={add.amount} onChange={(e) => setAdd({ ...add, amount: e.target.value })} />
            <AdminInput data-testid="add-credits-reference" label="Reference Number" value={add.reference} onChange={(e) => setAdd({ ...add, reference: e.target.value })} placeholder="EFT ref / payment ID" />
            <AdminTextarea data-testid="add-credits-customer-note" label="Customer-Visible Note" rows={2} value={add.customer_note} onChange={(e) => setAdd({ ...add, customer_note: e.target.value })} placeholder="Shown in the customer's credit history" />
            <AdminTextarea data-testid="add-credits-admin-note" label={<span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> Admin-Only Note</span>} rows={2} value={add.admin_note} onChange={(e) => setAdd({ ...add, admin_note: e.target.value })} placeholder="Never shown to customers" />
            <button data-testid="add-credits-submit" onClick={() => submit("add", add, setAdd)} className="btn-primary w-full !py-2.5 !text-[11px]">
              <Plus className="w-3.5 h-3.5" /> Add Credits
            </button>
          </div>
        </Panel>

        {/* Remove */}
        <Panel title="Remove Credits" testId="credits-remove-panel"
          action={<Coins className="w-4 h-4 text-[#18d3ff]" />}>
          <div className="space-y-4">
            <AdminSelect data-testid="remove-credits-customer" label="Customer" value={remove.customer_id} onChange={(e) => setRemove({ ...remove, customer_id: e.target.value })}>
              <option value="" className="bg-black">— select customer —</option>
              {customers.map((c) => <option key={c.id} value={c.id} className="bg-black">{c.name} {c.surname} · {c.email} · {c.credit_balance ?? 0} cr</option>)}
            </AdminSelect>
            <AdminInput data-testid="remove-credits-amount" label="Credit Amount" type="number" min={1} value={remove.amount} onChange={(e) => setRemove({ ...remove, amount: e.target.value })} />
            <AdminTextarea data-testid="remove-credits-customer-note" label="Customer-Visible Note" rows={2} value={remove.customer_note} onChange={(e) => setRemove({ ...remove, customer_note: e.target.value })} placeholder="Reason — visible to the customer" />
            <AdminTextarea data-testid="remove-credits-admin-note" label={<span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> Admin-Only Note</span>} rows={2} value={remove.admin_note} onChange={(e) => setRemove({ ...remove, admin_note: e.target.value })} placeholder="Never shown to customers" />
            <button data-testid="remove-credits-submit" onClick={() => submit("remove", remove, setRemove)} className="btn-ghost w-full !py-2.5 !text-[11px]">
              <Minus className="w-3.5 h-3.5" /> Remove Credits
            </button>
          </div>
        </Panel>
      </div>

      {/* Filters */}
      <Panel testId="credits-filters">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-3 flex-1 glass chrome-border rounded-full px-4 py-2">
            <Search className="w-4 h-4 text-white/45" />
            <input data-testid="credits-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email / note / reference…" className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none font-body" />
          </div>
          <AdminSelect data-testid="credits-filter-customer" value={filterCust} onChange={(e) => setFilterCust(e.target.value)} className="sm:w-72">
            <option value="all" className="bg-black">All customers</option>
            {customers.map((c) => <option key={c.id} value={c.id} className="bg-black">{c.name} {c.surname} · {c.email}</option>)}
          </AdminSelect>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <div data-testid="credits-total-tx" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Transactions</p><p className="font-display text-2xl text-chrome">{totals.count}</p></div>
          <div data-testid="credits-total-added" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Added</p><p className="font-display text-2xl text-[#7fe6ff]">+{totals.added}</p></div>
          <div data-testid="credits-total-removed" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Removed</p><p className="font-display text-2xl text-[#ff9fcf]">−{totals.removed}</p></div>
          <div data-testid="credits-total-net" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Net Change</p><p className="font-display text-2xl text-chrome">{totals.net >= 0 ? "+" : ""}{totals.net}</p></div>
        </div>
      </Panel>

      <Panel title="Transaction History" testId="credits-history-panel" className="!p-0 overflow-hidden">
        {loading ? <p className="text-sm text-white/40 p-6">Loading…</p> :
         filtered.length === 0 ? <div className="p-6"><EmptyState title="No credit transactions" message="Add credits to a customer to populate the log." /></div> :
         (<>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/3 text-[10px] tracking-luxe uppercase text-white/45">
                <tr>
                  <th className="text-left px-5 py-3">Date</th>
                  <th className="text-left px-5 py-3">Customer</th>
                  <th className="text-left px-5 py-3">Type</th>
                  <th className="text-left px-5 py-3">Amount</th>
                  <th className="text-left px-5 py-3">Before</th>
                  <th className="text-left px-5 py-3">After</th>
                  <th className="text-left px-5 py-3">Customer Note</th>
                  <th className="text-left px-5 py-3">Admin Note</th>
                  <th className="text-left px-5 py-3">Ref</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-t border-white/5" data-testid={`credits-tx-row-${t.id}`}>
                    <td className="px-5 py-3 text-white/70 whitespace-nowrap">{fmtDate(t.created_at)}</td>
                    <td className="px-5 py-3 text-white/85">{t.customer_email}</td>
                    <td className="px-5 py-3"><span className={`text-[10px] tracking-luxe uppercase ${t.type === "add" ? "text-[#7fe6ff]" : "text-[#ff9fcf]"}`}>{t.type === "add" ? "Added" : "Removed"}</span></td>
                    <td className="px-5 py-3 text-white">{t.type === "add" ? "+" : "−"}{t.amount}</td>
                    <td className="px-5 py-3 text-white/55">{t.balance_before ?? "—"}</td>
                    <td className="px-5 py-3 text-white">{t.balance_after ?? "—"}</td>
                    <td className="px-5 py-3 text-white/60 max-w-[200px] truncate">{t.customer_note || t.note || "—"}</td>
                    <td className="px-5 py-3 text-[#ffd479]/80 max-w-[200px] truncate">{t.admin_note || "—"}</td>
                    <td className="px-5 py-3 text-white/45">{t.reference || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-white/5">
            {filtered.map((t) => (
              <div key={t.id} className="p-4" data-testid={`credits-tx-card-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-white truncate">{t.customer_email}</p>
                  <span className={`text-[10px] tracking-luxe uppercase ${t.type === "add" ? "text-[#7fe6ff]" : "text-[#ff9fcf]"}`}>{t.type === "add" ? "+" : "−"}{t.amount}</span>
                </div>
                <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-1">{fmtDate(t.created_at)} · {t.balance_before ?? 0} → {t.balance_after ?? 0}</p>
                {(t.customer_note || t.note) && <p className="text-xs text-white/55 mt-1">{t.customer_note || t.note}</p>}
                {t.admin_note && <p className="text-xs text-[#ffd479]/80 mt-1"><Lock className="w-3 h-3 inline mr-1" /> {t.admin_note}</p>}
              </div>
            ))}
          </div>
         </>)}
      </Panel>
    </div>
  );
}
